import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import {
  User,
  Mail,
  Shield,
  ArrowRight,
  BookOpen,
  Bell,
  TrendingUp,
  FileText,
  Image,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { useMaterials } from '../../materials/hooks/useMaterials';
import { useEvents } from '../../events/hooks/useEvents';
import { useSettingsContext } from '../../../shared/context/SettingsContext';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { materials, loading: materialsLoading } = useMaterials();
  const { events, loading: eventsLoading } = useEvents(false);
  const { settings } = useSettingsContext();

  // Get recent materials (limit to 5)
  const recentMaterials = materials.slice(0, 5);

  // Get recent events (limit to 6)
  const recentEvents = events.slice(0, 6);

  // Get updates from settings (same as home page)
  const updates = [
    { title: settings?.update1Title, description: settings?.update1Description },
    { title: settings?.update2Title, description: settings?.update2Description },
    { title: settings?.update3Title, description: settings?.update3Description },
    { title: settings?.update4Title, description: settings?.update4Description },
    { title: settings?.update5Title, description: settings?.update5Description },
  ].filter(update => update.title);

  if (!user) {
    return null;
  }

  return (
    <>
      <SEO
        title="Dashboard - Tuition Point"
        description="User dashboard with account information and quick access"
      />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Welcome Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">
                  Welcome back, {user.name || user.email.split('@')[0]}!
                </h1>
                <p className="mt-1 text-blue-100 text-sm">
                  Track your learning progress and upcoming classes
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Admin Panel Button - Only for admin and moderator roles */}
                {(user.role === 'admin' || user.role?.startsWith('moderator')) && (
                  <Button
                    onClick={() => navigate('/admin')}
                    className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    Admin Panel
                  </Button>
                )}
                <div className="hidden md:block">
                  <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                    <User className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left Column - Main Content (2/3 width) */}
            <div className="lg:col-span-2 space-y-6">

                  {/* Study Materials Access - Dynamic */}
                  <Card className="border-t-4 border-t-purple-500 shadow-md overflow-hidden p-0">
                    <div className="bg-gradient-to-r from-purple-100 to-violet-100 px-6 py-5">
                      <CardTitle className="flex items-center gap-2">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <FileText className="h-5 w-5 text-purple-600" />
                        </div>
                        Study Materials
                      </CardTitle>
                      <CardDescription className="mt-1.5">Access your course materials</CardDescription>
                    </div>
                    <CardContent className="p-6">
                      {materialsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                          <span className="ml-2 text-sm text-gray-500">Loading materials...</span>
                        </div>
                      ) : recentMaterials.length === 0 ? (
                        <div className="text-center py-6">
                          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No materials available yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {recentMaterials.map((material) => {
                            const subjectColors: Record<string, { bg: string; text: string }> = {
                              'Mathematics': { bg: 'bg-red-100', text: 'text-red-600' },
                              'Physics': { bg: 'bg-blue-100', text: 'text-blue-600' },
                              'Chemistry': { bg: 'bg-green-100', text: 'text-green-600' },
                              'Biology': { bg: 'bg-emerald-100', text: 'text-emerald-600' },
                              'English': { bg: 'bg-amber-100', text: 'text-amber-600' },
                            };
                            const colors = subjectColors[material.subject] || { bg: 'bg-purple-100', text: 'text-purple-600' };

                            return (
                              <Link
                                key={material.id}
                                to={`/materials/view/${material.id}`}
                                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 ${colors.bg} rounded`}>
                                    <FileText className={`h-5 w-5 ${colors.text}`} />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{material.title}</p>
                                    <p className="text-xs text-muted-foreground">{material.subject} • {material.classSection}</p>
                                  </div>
                                </div>
                                <ExternalLink className="h-4 w-4 text-gray-400" />
                              </Link>
                            );
                          })}
                          <Button
                            variant="outline"
                            className="w-full mt-2 border-purple-200 text-purple-600 hover:bg-purple-50"
                            onClick={() => navigate('/materials')}
                          >
                            View All Materials
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Latest Updates Section */}
                  {updates.length > 0 && (
                    <Card className="border-t-4 border-t-yellow-500 shadow-md overflow-hidden p-0">
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 px-6 py-5">
                        <CardTitle className="flex items-center gap-2">
                          <div className="p-2 bg-yellow-100 rounded-lg">
                            <Bell className="h-5 w-5 text-yellow-600" />
                          </div>
                          Latest Updates
                        </CardTitle>
                        <CardDescription className="mt-1.5">Important announcements and news</CardDescription>
                      </div>
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {updates.slice(0, 3).map((update, index) => (
                            <div key={index} className="border-l-4 border-yellow-400 pl-4 py-2">
                              <h4 className="font-medium text-sm text-gray-900">{update.title}</h4>
                              {update.description && (
                                <p className="text-xs text-muted-foreground mt-1">{update.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Gallery Section (Events) */}
                  <Card className="border-t-4 border-t-pink-500 shadow-md overflow-hidden p-0">
                    <div className="bg-gradient-to-r from-pink-50 to-rose-50 px-6 py-5">
                      <CardTitle className="flex items-center gap-2">
                        <div className="p-2 bg-pink-100 rounded-lg">
                          <Image className="h-5 w-5 text-pink-600" />
                        </div>
                        Gallery
                      </CardTitle>
                      <CardDescription className="mt-1.5">Recent photos and memories</CardDescription>
                    </div>
                    <CardContent className="p-6">
                      {eventsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-pink-600" />
                          <span className="ml-2 text-sm text-gray-500">Loading gallery...</span>
                        </div>
                      ) : recentEvents.length === 0 ? (
                        <div className="text-center py-6">
                          <Image className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No gallery items yet</p>
                        </div>
                      ) : (
                        <div>
                          <div className="grid grid-cols-3 gap-2">
                            {recentEvents.map((event) => (
                              <Link
                                key={event.id}
                                to={`/events/${event.id}`}
                                className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
                              >
                                {event.coverImageUrl ? (
                                  <img
                                    src={event.coverImageUrl}
                                    alt={event.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-600">
                                    <Image className="h-6 w-6 text-white/50" />
                                  </div>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                  <p className="text-white text-xs font-medium truncate">{event.title}</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                          <Button
                            variant="outline"
                            className="w-full mt-4 border-pink-200 text-pink-600 hover:bg-pink-50"
                            onClick={() => navigate('/events')}
                          >
                            View Full Gallery
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
            </div>

            {/* Right Column - Sidebar (1/3 width) */}
            <div className="space-y-6">

              {/* Account Information Card */}
              <Card className="border-t-4 border-t-[#001f3f] shadow-md hover:shadow-lg transition-shadow overflow-hidden p-0">
                <div className="bg-gradient-to-r from-blue-100 to-sky-100 px-6 py-5">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <User className="h-5 w-5 text-[#001f3f]" />
                    </div>
                    Account Information
                  </CardTitle>
                  <CardDescription className="mt-1.5">
                    Your account details and status
                  </CardDescription>
                </div>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium text-sm">{user.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Role</p>
                        <p className="font-medium text-sm capitalize">{user.role}</p>
                      </div>
                    </div>

                    {user.name && (
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Name</p>
                          <p className="font-medium text-sm">{user.name}</p>
                        </div>
                      </div>
                    )}

                    {user.phone && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <p className="font-medium text-sm">{user.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Admin Panel Card - Only for Admin users */}
              {user.role === 'admin' && (
                <Card className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white border-0 shadow-md hover:shadow-lg transition-all cursor-pointer overflow-hidden p-0" onClick={() => navigate('/admin')}>
                  <div className="px-6 py-5">
                    <CardTitle className="flex items-center gap-2 text-white">
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Shield className="h-5 w-5" />
                      </div>
                      Admin Panel
                    </CardTitle>
                    <CardDescription className="text-emerald-100 mt-1.5">
                      Manage users, performance metrics, and settings
                    </CardDescription>
                  </div>
                  <CardContent className="p-6">
                    <Button
                      className="w-full bg-white text-emerald-600 hover:bg-emerald-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/admin');
                      }}
                    >
                      Go to Admin Panel
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Quick Links */}
              <Card className="border-t-4 border-t-sky-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-blue-100 to-sky-100 px-6 py-5">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-sky-100 rounded-lg">
                      <BookOpen className="h-5 w-5 text-sky-600" />
                    </div>
                    Quick Links
                  </CardTitle>
                </div>
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start border-gray-300" onClick={() => navigate('/services')}>
                      <BookOpen className="mr-2 h-4 w-4" />
                      View Courses
                    </Button>
                    <Button variant="outline" className="w-full justify-start border-gray-300" onClick={() => navigate('/achievements')}>
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Success Stories
                    </Button>
                    <Button variant="outline" className="w-full justify-start border-gray-300" onClick={() => navigate('/contact')}>
                      <Mail className="mr-2 h-4 w-4" />
                      Contact Support
                    </Button>
                  </div>
                </CardContent>
              </Card>

            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;